import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNotification } from '../../context/NotificationManager/NotificationManager'
import { Button } from '@mui/material'
import { dateToString, dateToWeekday, getToday } from '../../utils/date'
import PageLoading from '../../components/PageLoading/PageLoading'
import InformationTable from '../../components/Table/InformationTable'
import ModalEvaluacionAlumno from '../../modals/ModalEvaluacionAlumno/ModalEvaluacionAlumno'
import {
  createEvaluacion,
  getAlumnosAEvaluar
} from '../../utils/api/evaluacion'
import css from './Evaluacion.module.css'
import { Link } from 'react-router-dom'

const Evaluacion = () => {
  const notification = useNotification()
  const queryClient = useQueryClient()

  const [isCreateModalOpen, setCreateModalOpen] = useState(false)
  const [selectedAlumno, setSelectedAlumno] = useState({
    alumno: null,
    index: null
  })

  const fecha = getToday()

  const { isLoading, data: alumnosAEvaluarList = [] } = useQuery({
    queryKey: ['evaluacion-alumnos', 'alumnos-a-evaluar'],
    queryFn: () =>
      getAlumnosAEvaluar({ fecha: dateToString(fecha) })
        .then(datos => datos)
        .catch(err => {
          notification.error({
            title: 'Error al recuperar los alumnos a evaluar',
            content: err
          })
          return []
        })
  })

  const handleNextStudent = () => {
    let nextIndex = (selectedAlumno.index ?? 0) + 1
    while (
      nextIndex < alumnosAEvaluarList.length &&
      alumnosAEvaluarList[nextIndex].evaluado
    ) {
      nextIndex++
    }
    if (nextIndex < alumnosAEvaluarList.length) {
      const nextItem = alumnosAEvaluarList[nextIndex]
      setSelectedAlumno({ alumno: nextItem, index: nextIndex })
    } else {
      notification.success({
        title: 'Finalizado',
        content: 'Todas las evaluaciones han sido enviadas.'
      })
      handleCloseModal()
    }
  }

  const { isPending: isCreatingEvaluacion, mutate: crearEvaluacion } =
    useMutation({
      mutationFn: createEvaluacion,
      onSuccess: () => {
        notification.success({
          title: 'Evaluación creada',
          content: 'Se ha creado la evaluación'
        })
      },
      onError: err => {
        notification.error({
          title: 'Error al crear la evaluación',
          content: err
        })
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: ['evaluacion-alumnos', 'alumnos-a-evaluar']
        })
        handleNextStudent()
      }
    })

  const handleOpenModal = (alumno, index) => {
    setSelectedAlumno({ alumno, index })
    setCreateModalOpen(true)
  }

  const handleCloseModal = () => {
    setCreateModalOpen(false)
    setSelectedAlumno({ alumno: null, index: null })
  }

  const handleSubmitModal = evaluacion => {
    if (isCreatingEvaluacion) return
    const payload = {
      ...evaluacion,
      alumnoId: selectedAlumno.alumno?.alumnoId,
      fecha: dateToString(fecha)
    }
    crearEvaluacion(payload)
  }

  const firstPendingIndex = alumnosAEvaluarList.findIndex(
    item => !item.evaluado
  )

  const handleStartEvaluation = () => {
    if (firstPendingIndex === -1) return
    handleOpenModal(alumnosAEvaluarList[firstPendingIndex], firstPendingIndex)
  }

  const hasMoreToEvaluate = alumnosAEvaluarList
    .slice((selectedAlumno.index ?? 0) + 1)
    .some(item => !item.evaluado)

  return (
    <PageLoading isLoading={isLoading}>
      <div className={css.header}>
        <div className={css.headerRow}>
          <p className={css.date}>
            Registro diario de asistencia – {dateToString(fecha)} (
            {dateToWeekday(fecha)})
          </p>
          <Button
            variant='contained'
            color='primary'
            size='small'
            disabled={firstPendingIndex === -1}
            onClick={handleStartEvaluation}
            startIcon={<i className='material-icons'>rate_review</i>}
          >
            Valorar
          </Button>
        </div>
      </div>
      <div className={css.main}>
        <InformationTable
          size='small'
          details={[
            { title: 'Alumno', key: 'alumno' },
            { title: 'Asignaturas', key: 'asignaturas', sortDisabled: true },
            { title: 'Evaluado', key: 'status', sortDisabled: true }
          ]}
          data={alumnosAEvaluarList.map(item => ({
            alumno: (
              <Link className={css.link} to={`/alumnos/${item.alumnoId}/ficha`}>
                {item.alumnoNombre || '--'}
              </Link>
            ),
            asignaturas: (item.asignaturas || []).map(a => a.nombre).join(', '),
            status: (
              <i
                className={`material-icons ${item.evaluado ? css.statusDone : css.statusPending}`}
              >
                {item.evaluado ? 'check_circle' : 'cancel'}
              </i>
            )
          }))}
        />
      </div>

      <ModalEvaluacionAlumno
        open={isCreateModalOpen}
        alumno={{ nombre: selectedAlumno.alumno?.alumnoNombre }}
        asignaturas={(selectedAlumno.alumno?.asignaturas || []).map(a => ({
          asignatura: a
        }))}
        onClose={handleCloseModal}
        onSubmit={handleSubmitModal}
        disabled={isCreatingEvaluacion}
        submitLabel={hasMoreToEvaluate ? 'Siguiente' : 'Finalizar'}
      />
    </PageLoading>
  )
}

export default Evaluacion
